/**
 * Opportunities — `docs/API.md` §6.
 *
 * Fully implemented server-side: list (+ aggregates), create, read, patch,
 * delete, stage transitions + history, products/competitors/contact-roles,
 * and timeline all live in `salescrmapi/src/modules/opportunities`. Only the
 * `quotations` sub-resource answers `501 not_implemented` — §7 has no module.
 */
import apiClient from './axiosClient.js';
import { withMockFallback } from './mockFallback.js';

/** @param {object} [params] any of listOpportunitiesQuery's fields, e.g. `{ q, stage_id, is_closed }` */
export function listOpportunities(params = {}) {
  return withMockFallback(
    'GET /opportunities',
    async () => (await apiClient.get('/opportunities', { params })).data,
    () => ({
      data: [],
      page: { limit: params.limit ?? 50, next_cursor: null, prev_cursor: null, has_more: false, total: 0 },
      aggregates: { total: 0, weighted: 0, average_deal: 0, count: 0, by_stage: {}, by_forecast_category: {} },
    }),
  );
}

export function getOpportunity(id) {
  return withMockFallback(
    `GET /opportunities/${id}`,
    async () => (await apiClient.get(`/opportunities/${id}`)).data,
    () => null,
  );
}

/** @param {object} payload at minimum `{ name, account_id, pipeline_id, stage_id, expected_close_date }` */
export function createOpportunity(payload) {
  return withMockFallback(
    'POST /opportunities',
    async () =>
      (await apiClient.post('/opportunities', payload, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })).data,
    () => ({ ...payload, id: `OP-${Math.floor(Math.random() * 90000 + 10000)}`, row_version: 1 }),
  );
}

export function updateOpportunity(id, patch, rowVersion) {
  return withMockFallback(
    `PATCH /opportunities/${id}`,
    async () =>
      (await apiClient.patch(`/opportunities/${id}`, patch, {
        headers: { 'If-Match': `"${rowVersion}"` },
      })).data,
    () => ({ id, ...patch }),
  );
}

export function deleteOpportunity(id, { reason, rowVersion } = {}) {
  return withMockFallback(
    `DELETE /opportunities/${id}`,
    async () =>
      (await apiClient.delete(`/opportunities/${id}`, {
        data: { reason, ...(rowVersion ? { row_version: rowVersion } : {}) },
      })).data,
    () => ({ id, deleted: true }),
  );
}

/**
 * Move a deal to another stage. Closing as lost additionally needs
 * `lost_reason_id` (and optionally `lost_note`/`lost_to_competitor_id`) —
 * refused with `422 lost_reason_required` otherwise.
 * @param {string} id
 * @param {{stage_id:string, probability?:number|null, expected_close_date?:string, note?:string, lost_reason_id?:string, lost_note?:string, lost_to_competitor_id?:string, row_version?:number}} payload
 */
export function changeOpportunityStage(id, payload) {
  return withMockFallback(
    `POST /opportunities/${id}/stage`,
    async () => (await apiClient.post(`/opportunities/${id}/stage`, payload)).data,
    () => ({ id, stage: { id: payload.stage_id }, workflows_triggered: [] }),
  );
}

export function getOpportunityStageHistory(id) {
  return withMockFallback(
    `GET /opportunities/${id}/stage-history`,
    async () => (await apiClient.get(`/opportunities/${id}/stage-history`)).data,
    () => ({ data: [], summary: { total_age_days: 0, stages_visited: 0, moved_backwards: 0 } }),
  );
}

export function listOpportunityProducts(id) {
  return withMockFallback(
    `GET /opportunities/${id}/products`,
    async () => (await apiClient.get(`/opportunities/${id}/products`)).data,
    () => ({ data: [] }),
  );
}

export function addOpportunityProduct(id, payload) {
  return withMockFallback(
    `POST /opportunities/${id}/products`,
    async () => (await apiClient.post(`/opportunities/${id}/products`, payload)).data,
    () => ({ data: payload }),
  );
}

export function removeOpportunityProduct(id, productId) {
  return withMockFallback(
    `DELETE /opportunities/${id}/products/${productId}`,
    async () => (await apiClient.delete(`/opportunities/${id}/products/${productId}`)).data,
    () => ({ deleted: true }),
  );
}

export function listOpportunityCompetitors(id) {
  return withMockFallback(
    `GET /opportunities/${id}/competitors`,
    async () => (await apiClient.get(`/opportunities/${id}/competitors`)).data,
    () => ({ data: [] }),
  );
}

/** @param {string} id @param {{competitor_id:string, is_primary?:boolean, outcome?:string, notes?:string}} payload */
export function addOpportunityCompetitor(id, payload) {
  return withMockFallback(
    `POST /opportunities/${id}/competitors`,
    async () => (await apiClient.post(`/opportunities/${id}/competitors`, payload)).data,
    () => ({ data: [] }),
  );
}

export function removeOpportunityCompetitor(id, competitorId) {
  return withMockFallback(
    `DELETE /opportunities/${id}/competitors/${competitorId}`,
    async () => (await apiClient.delete(`/opportunities/${id}/competitors/${competitorId}`)).data,
    () => ({ deleted: true }),
  );
}

export function listOpportunityContactRoles(id) {
  return withMockFallback(
    `GET /opportunities/${id}/contact-roles`,
    async () => (await apiClient.get(`/opportunities/${id}/contact-roles`)).data,
    () => ({ data: [] }),
  );
}

export function addOpportunityContactRole(id, payload) {
  return withMockFallback(
    `POST /opportunities/${id}/contact-roles`,
    async () => (await apiClient.post(`/opportunities/${id}/contact-roles`, payload)).data,
    () => ({ data: [] }),
  );
}

export function removeOpportunityContactRole(id, contactId) {
  return withMockFallback(
    `DELETE /opportunities/${id}/contact-roles/${contactId}`,
    async () => (await apiClient.delete(`/opportunities/${id}/contact-roles/${contactId}`)).data,
    () => ({ deleted: true }),
  );
}

export function getOpportunityTimeline(id) {
  return withMockFallback(
    `GET /opportunities/${id}/timeline`,
    async () => (await apiClient.get(`/opportunities/${id}/timeline`)).data,
    () => ({ data: [] }),
  );
}
